/**
 * Dependency-free OAuth decisions that can be exercised outside a browser.
 */

export const AUTH_BROWSER_STORAGE_UNAVAILABLE =
  'auth/browser-storage-unavailable';

function errorCode(err: unknown): string {
  return (err as { code?: string } | null)?.code ?? '';
}

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err ?? '');
}

/** A redirect is useful only when the browser could not open a popup at all. */
export function shouldFallBackToRedirect(err: unknown): boolean {
  const code = errorCode(err);
  return (
    code === 'auth/popup-blocked'
    || code === 'auth/operation-not-supported-in-this-environment'
  );
}

/**
 * The addresses that serve the Hub AND proxy Firebase's `/__/auth/*` helper
 * on their own origin (hub/netlify.toml's 200 rewrite). Both are the same
 * Netlify site, so both carry the proxy.
 *
 * The build bakes in ONE authDomain (VITE_FIREBASE_AUTH_DOMAIN), and it is
 * `ldg-hub-staging.netlify.app`, set when that was the only address. The
 * custom domain came later and inherited it, which made the sign-in helper
 * CROSS-SITE there: shouldStartWithSameOriginRedirect below saw a mismatch,
 * so iPhones on hub.thestudioldg.com got the popup instead of the redirect,
 * and iOS Safari never hands a cross-site popup's answer back. The button sat
 * on "Opening Google..." for good (2026-09-23: an admin frozen on her iPhone
 * signed in at once on ldg-hub-staging.netlify.app, whose helper is
 * same-origin). Proven with Sentry resource spans (the iframe loading from
 * the other host) and an emulated-iPhone run against the live site.
 *
 * Every address listed here must ALSO be an Authorized redirect URI
 * (`https://<host>/__/auth/handler`) on the Google OAuth web client, or
 * Google answers "Error 400: redirect_uri_mismatch" for every Google sign-in
 * on that address, desktop included. See HUB-SETUP.md.
 */
export const SAME_ORIGIN_AUTH_HOSTS: readonly string[] = [
  'hub.thestudioldg.com',
  'ldg-hub-staging.netlify.app',
];

/**
 * The authDomain the Firebase app should use on this page: the page's own
 * host when it is one of SAME_ORIGIN_AUTH_HOSTS, so the helper is always
 * first-party, otherwise the configured value (deploy previews, localhost).
 */
export function resolveAuthDomain(input: {
  appHost: string;
  configuredAuthDomain: string;
}): string {
  const appHost = input.appHost.trim().toLowerCase();
  return SAME_ORIGIN_AUTH_HOSTS.includes(appHost)
    ? appHost
    : input.configuredAuthDomain;
}

/**
 * Phone browsers should use redirect only after the helper is on this origin.
 * That avoids a fragile popup in in-app WebKit without reviving the original
 * cross-site storage failure during a staged rollout.
 */
export function shouldStartWithSameOriginRedirect(input: {
  userAgent: string;
  appHost: string;
  authDomain: string;
}): boolean {
  const mobile = /iPhone|iPad|iPod|Android/i.test(input.userAgent);
  const appHost = input.appHost.trim().toLowerCase();
  const authDomain = input.authDomain.trim().toLowerCase();
  return mobile && appHost !== '' && appHost === authDomain;
}

/** Safari and in-app browsers can lose Firebase's sessionStorage handshake. */
export function isBrowserStorageAuthError(err: unknown): boolean {
  return /missing initial state/i.test(errorMessage(err));
}

/** Replace Firebase's technical storage error with a stable UI-facing code. */
export function normalizeOAuthSignInError(err: unknown): unknown {
  if (!isBrowserStorageAuthError(err)) return err;
  return Object.assign(
    new Error(AUTH_BROWSER_STORAGE_UNAVAILABLE),
    { code: AUTH_BROWSER_STORAGE_UNAVAILABLE }
  );
}
